import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ModalCamadas } from './ModalCamadas';
import { Skeleton } from '@mui/material';
import { EnumContainerTipo } from '../../../../../shared/enums';

type Container = {
  id: string;
  tipo: string;
};

type AcessoComponente = {
  id: string;
  nome: string;
};

type CamadaSimples = {
  id: string;
  nome: string;
  containerId: string;
  containerNome?: string;
  acessoComponentes?: AcessoComponente[];
};

type Row = {
  id: string;
  nome: string;
  containerId: string;
  containerNome?: string;
  acessoComponentes?: AcessoComponente[];
};

type Props = {
  initialRows?: CamadaSimples[];
  availableContainers?: Container[];
  onCamadasChange?: (camadas: CamadaSimples[]) => void;
  currentSystemId?: string;
  isLoading?: boolean;
};

const convertToRows = (camadas: CamadaSimples[]): Row[] => {
  return camadas.map((item) => ({
    id: item.id,
    nome: item.nome,
    containerId: item.containerId,
    containerNome: item.containerNome,
    acessoComponentes: item.acessoComponentes || [],
  }));
};

export const ListaCamadas: React.FC<Props> = ({
  initialRows = [],
  availableContainers = [],
  onCamadasChange,
  currentSystemId,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);

  // Função para obter o nome do container pelo ID do enum
  const getContainerNameById = (id: string): string => {
    const enumValue = parseInt(id);
    const containerEntry = Object.entries(EnumContainerTipo).find(
      ([key, value]) => value === enumValue && typeof value === 'number'
    );
    return containerEntry ? containerEntry[0] : 'N/A';
  };

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (row: Row) => {
    setEditingRow(row);
    setOpenModal(true);
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onCamadasChange) {
      const camadas = newRows.map((row) => ({
        id: row.id,
        nome: row.nome,
        containerId: row.containerId,
        containerNome: row.containerNome,
        acessoComponentes: row.acessoComponentes || [],
      }));
      onCamadasChange(camadas);
    }
  };

  const handleAddNew = () => {
    setEditingRow(null);
    setOpenModal(true);
  };

  const handleModalSave = (values: any) => {
    // Obter o nome do container pelo ID do enum
    const containerName = getContainerNameById(values.containerId);

    let newRows: any[] = [];
    if (values.id) {
      // Editando item existente
      newRows = rows.map((r) =>
        r.id === values.id
          ? {
            ...r,
            nome: values.nome,
            containerId: values.containerId,
            containerNome: containerName,
            acessoComponentes: values.acessoComponentes || [],
          }
          : r
      );
    } else {
      // Novo item
      const newRow: Row = {
        id: String(Date.now()),
        nome: values.nome,
        containerId: values.containerId,
        containerNome: containerName,
        acessoComponentes: values.acessoComponentes || [],
      };
      newRows = [newRow, ...rows];
    }

    setRows(newRows);
    if (onCamadasChange) {
      const camadas = newRows.map((row) => ({
        id: row.id,
        nome: row.nome,
        containerId: row.containerId,
        containerNome: row.containerNome,
        acessoComponentes: row.acessoComponentes || [],
      }));
      onCamadasChange(camadas);
    }
    setOpenModal(false);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setEditingRow(null);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleAddNew}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}

      <ModalCamadas
        open={openModal}
        onClose={handleModalClose}
        onSave={handleModalSave}
        availableContainers={availableContainers}
        initialValues={
          editingRow
            ? {
              id: editingRow.id,
              nome: editingRow.nome,
              containerId: editingRow.containerId,
              acessoComponentes: editingRow.acessoComponentes || [],
            }
            : undefined
        }
      />

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Container</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3].map((n) => (
                  <TableRow key={n}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={150} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={120} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhuma camada cadastrada
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.nome}</TableCell>
                  <TableCell>{row.containerNome || 'N/A'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
